import React, { useState } from 'react';
import { Users, Rocket, Code, CheckCircle2 } from 'lucide-react';
import { WaitlistForm } from '../components/WaitlistForm';
import { useWaitlistCount } from '../hooks/useWaitlistCount';
import { WaitlistStats } from '../components/WaitlistStats';
import { FadeIn } from '../components/animations/FadeIn';

function Progress() {
  const [submitted, setSubmitted] = useState(false);
  const { count } = useWaitlistCount();
  const goalSignups = 2000;
  const progress = (count / goalSignups) * 100;

  const getTierInfo = (count: number) => {
    if (count < 500) return { name: 'Grundare', spots: 500 - count };
    if (count < 1000) return { name: 'Pionjär', spots: 1000 - count };
    if (count < 2000) return { name: 'Tidig användare', spots: 2000 - count };
    return { name: 'Väntelista', spots: 0 };
  };

  const { name: currentTier, spots: remainingSpots } = getTierInfo(count);

  const milestones = [
    {
      title: "Validering av koncept",
      description: "Vi samlar intresse via väntelistan och validerar det tekniska konceptet",
      status: "current",
      icon: Users,
      progress: `${count} av ${goalSignups} anmälda`,
      details: "Vi har bekräftat att vi kan bygga en AI-modell tränad på svenska bidragsansökningar och nu validerar vi marknadsintresset.",
      features: [
        "AI-modell för analys av ansökningar",
        "Automatisk matchning av bidrag",
        "Intelligent ansökningsassistent"
      ]
    },
    {
      title: "Utveckling startar",
      description: "När vi når 2000 intresseanmälningar påbörjar vi utvecklingen",
      status: "upcoming",
      icon: Code,
      progress: null,
      details: "Full utveckling av plattformen med AI-integration, användarhantering och ansökningsverktyg.",
      features: [
        "Användarhantering och profiler",
        "Bidragsdatabas och sökmotor",
        "AI-driven ansökningshjälp"
      ]
    },
    {
      title: "Beta-lansering",
      description: "Första versionen släpps till väntelistan",
      status: "upcoming",
      icon: Rocket,
      progress: null,
      details: "Tidig åtkomst för väntelistans medlemmar med kontinuerliga uppdateringar baserat på feedback.",
      features: [
        "Tidig åtkomst för väntelistan",
        "Kontinuerliga uppdateringar",
        "Feedback-driven utveckling"
      ]
    }
  ];

  const currentMilestone = milestones.find(m => m.status === 'current');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Current Status */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 py-16 md:py-24">
            {/* Left Column - Status */}
            <div>
              <FadeIn>
                <div className="mb-8">
                  <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                    Var med och avgör tjänstens framtid
                  </h1>
                  <p className="text-xl text-gray-600">
                    Just nu är vi i fasen <span className="font-semibold text-blue-600">{currentMilestone?.title}</span> och samlar intresse för att starta utvecklingen.
                  </p>
                </div>

                {/* Progress Card */}
                <div className="bg-gray-900 rounded-2xl p-6 md:p-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Anmälningar till väntelistan</p>
                      <p className="text-white text-3xl font-bold">{count} / {goalSignups}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-400 text-sm mb-1">Utvecklingsstart</p>
                      <p className="text-white text-3xl font-bold">{Math.round(progress)}%</p>
                    </div>
                  </div>
                  <div className="bg-gray-800 rounded-full h-4 overflow-hidden mb-4">
                    <div 
                      className="bg-blue-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-gray-300 text-sm">
                    Vi bygger tjänsten vid {goalSignups} anmälda på väntelistan
                  </p>
                </div>
              </FadeIn>
            </div>

            {/* Right Column - Development Timeline */}
            <div className="relative">
              <FadeIn direction="left">
                <div className="sticky top-24">
                  <h2 className="text-2xl font-bold mb-8 text-gray-900">Utvecklingsplan</h2>
                  <div className="space-y-6">
                    {milestones.map((milestone, index) => (
                      <FadeIn key={index} delay={index * 0.1}>
                        <div 
                          className={`relative bg-white rounded-xl p-6 border ${
                            milestone.status === 'current'
                              ? 'border-blue-200 ring-2 ring-blue-500 ring-opacity-50'
                              : 'border-gray-100'
                          }`}
                        >
                          {/* Vertical Line */}
                          {index < milestones.length - 1 && (
                            <div className="absolute bottom-0 left-11 w-0.5 h-6 bg-gray-200 -mb-6 z-0" />
                          )}

                          <div className="flex gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              milestone.status === 'current' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                            }`}>
                              <milestone.icon size={20} />
                            </div>
                            <div>
                              <div className="flex items-center gap-3 mb-1">
                                <h3 className="font-semibold text-gray-900">{milestone.title}</h3>
                                {milestone.status === 'current' && (
                                  <span className="bg-blue-100 text-blue-600 text-xs font-medium px-2 py-0.5 rounded-full">
                                    Pågående
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{milestone.description}</p>
                              {milestone.progress && (
                                <p className="text-sm font-medium text-blue-600">{milestone.progress}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </FadeIn>
                    ))}
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4">
          {!submitted ? (
            <FadeIn>
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-blue-100">
                <WaitlistStats className="mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Säkra din plats och få 3 månader gratis
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  Endast {remainingSpots} platser kvar med {currentTier === 'Grundare' ? '3' : currentTier === 'Pionjär' ? '2' : '1'} månader gratis användning och livstids VIP-support
                </p>
                <WaitlistForm onSuccess={() => setSubmitted(true)} />
              </div>
            </FadeIn>
          ) : (
            <FadeIn>
              <div className="bg-green-50 p-8 rounded-2xl border border-green-100 shadow-lg text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <CheckCircle2 className="text-green-500" size={28} />
                  <p className="text-gray-900 font-semibold text-xl">
                    Grattis! Du är nu registrerad som {currentTier}
                  </p>
                </div>
                <p className="text-gray-600 text-lg">
                  Vi håller dig uppdaterad om projektets utveckling.
                </p>
              </div>
            </FadeIn>
          )}
        </div>
      </section>
    </div>
  );
}

export default Progress;